// =====================================================================
// EncounterPage (PME Sprint 2 / S51) — página da consulta (SOAP).
// Atende 2 rotas:
//   /prontuario/paciente/:patientId/consulta/nova  -> criar
//   /prontuario/consulta/:id                         -> ver/editar/assinar
// Carrega profissionais (dropdown) + agendamentos do paciente (vínculo/agenda).
// Delega o form ao EncounterForm; cuida de API + navegação.
// =====================================================================
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

import EncounterForm from "./EncounterForm";
import EncounterView from "./EncounterView";
import {
  getEncounter,
  createEncounter,
  updateEncounter,
  signEncounter,
  listEncounterProfessionals,
  listPatientAppointments,
  encounterErrorMessage
} from "../../services/encounterService";
import { linkScribeSession } from "../../services/scribeService";
import useIsMobile from "../../utils/useIsMobile";

const EncounterPage = () => {
  const { patientId, id } = useParams();
  const history = useHistory();
  const isMobile = useIsMobile();
  const isEdit = !!id;

  const [encounter, setEncounter] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [effectivePatientId, setEffectivePatientId] = useState(patientId || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmSign, setConfirmSign] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const profRes = await listEncounterProfessionals();
        let enc = null;
        let pid = patientId || null;
        if (isEdit) {
          enc = await getEncounter(id);
          pid = enc.patientId;
        }
        let appts = [];
        if (pid) {
          try {
            const ar = await listPatientAppointments(pid);
            appts = (ar && ar.appointments) || [];
          } catch (e) {
            // integração agenda é best-effort — não bloqueia a consulta
          }
        }
        if (active) {
          setProfessionals((profRes && profRes.professionals) || []);
          setEncounter(enc);
          setAppointments(appts);
          setEffectivePatientId(pid);
        }
      } catch (err) {
        toast.error(encounterErrorMessage(err));
        history.push(patientId ? `/prontuario/paciente/${patientId}` : "/prontuario");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, patientId, isEdit, history]);

  const goBack = () => {
    history.push(effectivePatientId ? `/prontuario/paciente/${effectivePatientId}` : "/prontuario");
  };

  const handleSave = async (payload, scribeSessionId) => {
    setSaving(true);
    try {
      let savedId;
      if (isEdit) {
        const updated = await updateEncounter(id, payload);
        setEncounter(updated);
        savedId = id;
        toast.success("Consulta salva.");
      } else {
        const created = await createEncounter(patientId, payload);
        savedId = created.id;
        toast.success("Consulta criada.");
      }
      // Registro de uso de IA (CFM 2.454) — best-effort, NUNCA bloqueia a consulta.
      if (scribeSessionId && savedId) {
        try {
          await linkScribeSession(scribeSessionId, savedId);
        } catch (e) {
          // se o link falhar, a consulta já está salva — só não marca ai_*
        }
      }
      if (!isEdit && savedId) {
        history.push(`/prontuario/consulta/${savedId}`);
      }
    } catch (err) {
      toast.error(encounterErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async () => {
    setConfirmSign(false);
    setSaving(true);
    try {
      const signed = await signEncounter(id);
      setEncounter(signed);
      toast.success("Consulta assinada.");
    } catch (err) {
      toast.error(encounterErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const readOnly = isEdit && encounter && encounter.status !== "open";

  return (
    <MainContainer>
      <MainHeader>
        <Title>{isEdit ? "Consulta" : "Nova consulta"}</Title>
        <MainHeaderButtonsWrapper>
          <Button onClick={goBack} disabled={saving}>
            Voltar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <CircularProgress />
          </div>
        ) : readOnly ? (
          <EncounterView
            encounter={encounter}
            professionals={professionals}
            appointments={appointments}
          />
        ) : (
          <EncounterForm
            key={encounter ? encounter.id : "new"}
            mode={isEdit ? "edit" : "create"}
            initialEncounter={encounter}
            patientId={effectivePatientId}
            professionals={professionals}
            appointments={appointments}
            saving={saving}
            readOnly={readOnly}
            onSave={handleSave}
            onCancel={goBack}
            onSign={isEdit && !readOnly ? () => setConfirmSign(true) : undefined}
          />
        )}
      </div>

      <Dialog open={confirmSign} onClose={() => setConfirmSign(false)} fullScreen={isMobile}>
        <DialogTitle>Assinar consulta</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ao assinar, a consulta vira um registro <strong>imutável</strong> do prontuário e não
            poderá mais ser editada. Confirma?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSign(false)}>Cancelar</Button>
          <Button onClick={handleSign} color="primary" variant="contained">
            Assinar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default EncounterPage;
